import { useEffect, useState, type ReactNode } from 'react';

/**
 * AdSlot — the swappable ad-provider seam (D-038/D-138). Public-join viewers
 * must see an ad before a published game loads (brief requirement); the
 * PROVIDER is pluggable so a real network (AdSense / Unity Ads / …) can swap
 * in without touching the viewer. Ships with the placeholder provider only —
 * no real ad service is wired (see 3rd-party-services doc, SCAFFOLDED).
 *
 * HOW TO SWAP A REAL PROVIDER: implement AdProvider.render to mount the
 * network's tag inside the slot, call `onDone` when the network reports the
 * ad completed (or keep the timer as the floor). Skip-proofing (a signed
 * ad-cleared token checked by the /url endpoint) is a documented follow-up —
 * with the placeholder there is nothing real to skip yet.
 */
export interface AdProvider {
  name: string;
  /** Render the ad creative; call onDone when the ad experience completes. */
  render(opts: { secondsLeft: number }): ReactNode;
}

export const placeholderProvider: AdProvider = {
  name: 'placeholder',
  render: ({ secondsLeft }) => (
    <div className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed bg-muted/40 text-center">
      <span className="text-sm font-medium text-muted-foreground">Ad space</span>
      <span className="text-xs text-muted-foreground">
        Your game loads in {secondsLeft}…
      </span>
    </div>
  ),
};

export function AdSlot({
  provider = placeholderProvider,
  seconds = 5,
  onCleared,
}: {
  provider?: AdProvider;
  seconds?: number;
  onCleared: () => void;
}) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (left <= 0) {
      onCleared();
      return;
    }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left]);
  return <>{provider.render({ secondsLeft: left })}</>;
}
